import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, InputAdornment, LinearProgress, MenuItem, Select, TextField, Typography } from "@mui/material";
import { inject, observer } from "mobx-react";
import React, { useCallback, useState } from "react";
import WithManufacturer from "../../contexts/WithManufacturer";
import { Product } from "../../stores/product/Product";
import { RootStore } from "../../stores/rootStore";
import { getUserSelectedWarehouse, setUserSelectedWarehouse } from '../../utils/LocalStorageUtils';
import { useGetV4WarehousesQuery } from "randmar-api-client";

interface AddToRequisitionProps {
  store?: RootStore;
  applicationId: string;
  product: Product;
  children: React.ReactNode;
}

const AddToRequisition: React.FC<AddToRequisitionProps> = ({ store, applicationId, product, children }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [chosenWarehouse, setChosenWarehouse] = useState<string>(getUserSelectedWarehouse());
  const initialQty = product.masterCarton || 1;
  const [quantityDisplay, setQuantityDisplay] = useState<number>(initialQty);
  const [quantityReal, setQuantityReal] = useState<number>(initialQty);

  const handleCancel = useCallback(() => setOpen(false), []);
  const handleAddToRequisitionButton = useCallback(() => {
    setOpen(true);
    setChosenWarehouse(getUserSelectedWarehouse());
  }, []);

  const propertyChange = useCallback((propertyName: string, newValue: string) => {
    switch (propertyName) {
      case "warehouse":
        setChosenWarehouse(newValue);
        setUserSelectedWarehouse(newValue);
        break;
      case "quantity": {
        const mc = product.masterCarton;
        if (isNaN(parseInt(newValue)) || parseInt(newValue) < 0) newValue = (mc || 0).toString();
        const numericValue = parseInt(newValue);
        if (mc !== null && mc !== undefined && mc > 0) {
          setQuantityDisplay(numericValue);
          setQuantityReal(Math.floor(numericValue / mc) * mc);
        } else {
          setQuantityDisplay(numericValue);
          setQuantityReal(numericValue);
        }
        break;
      }
    }
  }, [product.masterCarton]);

  const handleConfirmRequisitionRequest = useCallback(() => {
    setLoading(true);
    const { productStore, snackStore, documentsStore } = store!;
    productStore
      .addToRequisition(applicationId, product.randmarSKU || "", chosenWarehouse, quantityReal.toString())
      .then((requisitionNumber) => {
        if (requisitionNumber) {
          snackStore.showSuccess(`Requestition successfully made.`);
          if (documentsStore.requisitions.length === 0) documentsStore.getRequisitions(applicationId);
        } else snackStore.showError(`Could not request product`);

        productStore.getProduct(applicationId, product.randmarSKU || "");
        setLoading(false);
        setOpen(false);
      });
  }, [applicationId, chosenWarehouse, product.randmarSKU, quantityReal, store, product]);

  const { data: warehouses } = useGetV4WarehousesQuery();

  if (!warehouses) return null;
  console.log(warehouses);

  return (
    <>
      <Box display={"contents"} onClick={handleAddToRequisitionButton}>
        {children}
      </Box>
      <Dialog
        open={open}
        onClose={loading ? undefined : handleCancel}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title" style={{ textAlign: "center" }}>
          <h3>Request an order for item {product.mpn}</h3>
        </DialogTitle>
        <div hidden={loading}>
          <DialogContent style={{ textAlign: "center" }}>
            <div>Master carton quantity is {product.masterCarton}</div>
            <Select
              value={chosenWarehouse}
              fullWidth
              label="Warehouse"
              onChange={e => propertyChange("warehouse", e.target.value as string)}>
              {warehouses.map(warehouse => <MenuItem key={"Warehouse" + warehouse.WarehouseId} value={warehouse.WarehouseId!}>{warehouse.Name}</MenuItem>)}
            </Select>
            <TextField
              label="Quantity"
              fullWidth
              value={quantityDisplay}
              sx={{ mt: 2, mb: 2 }}
              onChange={e => propertyChange("quantity", e.target.value as string)}
              InputProps={{ endAdornment: <InputAdornment position="start">units</InputAdornment> }}
            />
            <Divider sx={{ mt: 1, mb: 1 }} />
            <div>
              Reorder quantity is <b>{quantityReal}</b>
              <br />
              Total master cartons is <b>{quantityReal / (product.masterCarton || 1)}</b>
            </div>
          </DialogContent>
          <DialogActions style={{ display: "flex", justifyContent: "space-between" }}>
            <Button onClick={handleCancel} color="primary">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmRequisitionRequest}
              color="primary"
              disabled={quantityReal <= 0}>
              Confirm
            </Button>
          </DialogActions>
        </div>
        <div hidden={!loading} style={{ textAlign: "center" }}>
          <Typography>Please wait...</Typography>
          <LinearProgress />
        </div>
      </Dialog>
    </>
  );
};

export default WithManufacturer(inject("store")(observer(AddToRequisition)));